import { useState, useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import SEO from "@/components/base/SEO";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import { categories, allManhwa } from "@/mocks/manhwa";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState(slug || "romance");

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  const currentCat = categories.find((c) => c.slug === activeCategory);
  const filteredManhwa = useMemo(
    () => allManhwa.filter((m) => m.category === activeCategory),
    [activeCategory]
  );

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
    navigate(`/category/${newSlug}`);
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  const catTitles: Record<string, string> = {};
  categories.forEach((c) => {
    catTitles[c.slug] = c.name;
  });

  const title = `免费${currentCat?.name || "韩漫"} - 韩漫漫画网`;
  const description = `韩漫漫画网${currentCat?.name || "漫画"}分类：${currentCat?.description || "精选韩国漫画"}，免费在线高清阅读。`;

  return (
    <>
      <SEO
        title={title}
        description={description}
        keywords={`${currentCat?.name || "韩漫"},免费韩漫,在线漫画,韩国漫画,韩漫漫画网`}
        canonicalUrl={typeof window !== "undefined" ? `${window.location.origin}/category/${activeCategory}` : ""}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: currentCat?.name || "分类" },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-8 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="mb-10">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                {currentCat?.name}漫画
              </h1>
              <p className="text-sm text-foreground-600">
                {currentCat?.description}
              </p>
            </div>

            <div className="flex flex-wrap gap-2 mb-10">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors duration-200 cursor-pointer ${
                    activeCategory === cat.slug
                      ? "bg-primary-500 text-white"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={cat.icon}></i>
                  </div>
                  {cat.name}
                </button>
              ))}
            </div>

            {filteredManhwa.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5 md:gap-6" data-product-shop>
                {filteredManhwa.map((manhwa) => (
                  <Link
                    key={manhwa.id}
                    to={`/article/${manhwa.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative w-full aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-200">
                      <img
                        src={manhwa.cover}
                        alt={manhwa.title}
                        title={`${manhwa.title} - 免费韩漫${currentCat?.name || ""}在线漫画`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-2 right-2">
                        <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-accent-500 text-white">
                          {manhwa.rating}
                        </span>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                        <span className="text-[10px] text-white/80 bg-white/20 px-1.5 py-0.5 rounded">
                          {manhwa.status} · {manhwa.chapters}话
                        </span>
                      </div>
                    </div>
                    <h3 className="font-heading text-sm font-bold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1 mb-1">
                      {manhwa.title}
                    </h3>
                    <p className="text-xs text-foreground-500">{manhwa.author}</p>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无漫画，请浏览其他分类</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}